import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { API_URL, errorDetail, fetchJson } from "@/lib/admin-api";
import { formatNumber } from "@/lib/format";
import {
  boxFromCorners,
  boxProblem,
  oppositeCorner,
  toNatural,
  type Corner,
  type Point,
} from "@/lib/box-editor";
import { resolveCrop, type BoundingArea, type Natural } from "@/lib/image-crop";
import { kg } from "@/components/admin/device-last-events";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * The office test for a device, in its admin sheet (POS2-1354 CP2): the camera half is its
 * latest real photo with the crop box drawn over it, the scales half its last 15 minutes of
 * one-second weight samples (POS2-1004).
 */

type Variant = "scrappy-sense-hat" | "scrappy-epaper" | "legacy-logger";

interface CameraCheck {
  capture: { image_url: string; logged_at: string; source: "device" | "imported" } | null;
  bounding_area: BoundingArea | null;
}

interface TelemetrySamples {
  minutes: { weight_samples: (number | null)[] }[];
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

export function DeviceCameraCheck({ deviceId }: { deviceId: string }) {
  const headers = useApiHeaders();
  const check = useQuery<CameraCheck, Error>({
    queryKey: ["admin", "devices", deviceId, "last-capture"],
    queryFn: () =>
      fetchJson<CameraCheck>(`${API_URL}/api/v1/admin/devices/${deviceId}/last-capture`, headers),
    retry: false,
  });
  // Its own route because it asks balena, which can be slow; fetched once per device.
  const variant = useQuery<{ variant: Variant | null }, Error>({
    queryKey: ["admin", "devices", deviceId, "variant"],
    queryFn: () =>
      fetchJson<{ variant: Variant | null }>(
        `${API_URL}/api/v1/admin/devices/${deviceId}/variant`,
        headers,
      ),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="text-xs font-medium tracking-wide text-gray-400 uppercase">Camera</p>
        {check.isLoading && <p className="text-gray-400">Loading the last capture…</p>}
        {check.isError && <p className="text-red-700">{errorDetail(check.error)}</p>}
        {check.data && (
          // Keyed on the saved box, so a save or a refetch starts the draft again from it.
          <BoxPanel
            key={String(check.data.bounding_area)}
            deviceId={deviceId}
            check={check.data}
            // Unknown until balena answers, or when it cannot: the note says so.
            variant={variant.isPending ? "loading" : (variant.data?.variant ?? null)}
          />
        )}
      </div>
      <ScalesCheck deviceId={deviceId} />
    </div>
  );
}

function VariantNote({ variant }: { variant: Variant | null | "loading" }) {
  if (variant === "loading") return null;
  if (variant === "legacy-logger") {
    return (
      <p className="text-xs text-gray-500">
        legacy-logger stores the full frame. It also uses this box as its frame-comparison area, and
        picks it up within an hour.
      </p>
    );
  }
  if (variant === null) {
    return (
      <p className="text-xs text-amber-700">
        Device software unknown (no balena device, or balena did not answer): this capture may
        already be cropped on the device.
      </p>
    );
  }
  return (
    <p role="note" className="text-xs text-amber-700">
      {variant} crops frames on the device with its CROP_AREA_* variables, so this capture may
      already be cropped and a box here would crop it again. Do not set one while CROP_AREA_* is
      set.
    </p>
  );
}

function BoxPanel({
  deviceId,
  check,
  variant,
}: {
  deviceId: string;
  check: CameraCheck;
  variant: Variant | null | "loading";
}) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<BoundingArea | null>(check.bounding_area);
  const [natural, setNatural] = useState<Natural | null>(null);
  const [confirming, setConfirming] = useState(false);
  // These crop on the device, so a box here would crop an already-cropped frame (POS2-1013).
  const cropsOnDevice = variant === "scrappy-sense-hat" || variant === "scrappy-epaper";

  const save = useMutation<unknown, Error, BoundingArea | null>({
    mutationFn: async (box) => {
      const res = await fetch(`${API_URL}/api/v1/admin/devices/${deviceId}/bounding-area`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ bounding_area: box }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
    },
    onSuccess: () => {
      setConfirming(false);
      return queryClient.invalidateQueries({ queryKey: ["admin", "devices"] });
    },
  });

  const problem = draft && boxProblem(draft, natural);
  const unchanged = String(draft) === String(check.bounding_area);
  // The saved box as every other capture screen draws it: an overrunning box is not applied.
  const savedOverruns =
    check.bounding_area !== null && natural !== null && !resolveCrop(check.bounding_area, natural);

  return (
    <div className="space-y-3">
      <VariantNote variant={variant} />
      {check.capture ? (
        <>
          <p className="text-xs text-gray-400">
            {check.capture.source === "device" ? "Direct" : "DataBridge"} capture,{" "}
            {formatTime(check.capture.logged_at)}. Drag to draw the box, drag a corner to resize.
          </p>
          <BoxEditor
            src={check.capture.image_url}
            box={draft}
            natural={natural}
            onNatural={setNatural}
            onChange={setDraft}
          />
          {natural && (
            <p className="text-xs text-gray-400">
              Original {natural.width}×{natural.height} px
            </p>
          )}
        </>
      ) : (
        <p className="text-gray-500">No capture yet. The box can still be typed in.</p>
      )}
      {savedOverruns && (
        <p className="text-xs text-amber-700">
          The saved box runs past this frame, so classification and the capture screens ignore it.
        </p>
      )}
      <BoxInputs box={draft} onChange={setDraft} />
      {problem && <p className="text-xs text-red-700">{problem}</p>}
      {save.isError && <p className="text-xs text-red-700">{errorDetail(save.error)}</p>}
      {confirming && (
        <p role="alert" className="text-xs font-medium text-amber-800">
          This device crops on the device. Save the box only if its CROP_AREA_* variables are unset,
          or the frame is cropped twice.
        </p>
      )}
      <div className="flex gap-2">
        <Button
          onClick={() =>
            cropsOnDevice && draft !== null && !confirming
              ? setConfirming(true)
              : save.mutate(draft)
          }
          disabled={unchanged || !!problem || save.isPending}
        >
          {confirming ? "Save anyway" : "Save box"}
        </Button>
        <Button variant="outline" onClick={() => setDraft(null)} disabled={draft === null}>
          Clear
        </Button>
      </div>
    </div>
  );
}

const CORNERS: { corner: Corner; className: string }[] = [
  { corner: "nw", className: "-top-1.5 -left-1.5 cursor-nwse-resize" },
  { corner: "ne", className: "-top-1.5 -right-1.5 cursor-nesw-resize" },
  { corner: "sw", className: "-bottom-1.5 -left-1.5 cursor-nesw-resize" },
  { corner: "se", className: "-right-1.5 -bottom-1.5 cursor-nwse-resize" },
];

/** The capture with the box over it. All coordinates are the original's pixels. */
export function BoxEditor({
  src,
  box,
  natural,
  onNatural,
  onChange,
}: {
  src: string;
  box: BoundingArea | null;
  natural: Natural | null;
  onNatural: (natural: Natural) => void;
  onChange: (box: BoundingArea | null) => void;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<Point | null>(null);

  function pointAt(event: React.PointerEvent): Point {
    const rect = frame.current!.getBoundingClientRect();
    const offset = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    return toNatural(offset, rect, natural!);
  }

  function start(event: React.PointerEvent) {
    if (!natural) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const corner = (event.target as HTMLElement).dataset.corner as Corner | undefined;
    setAnchor(corner && box ? oppositeCorner(box, corner) : pointAt(event));
  }

  function move(event: React.PointerEvent) {
    if (!anchor) return;
    const next = boxFromCorners(anchor, pointAt(event));
    if (next) onChange(next);
  }

  const pct = (value: number, of: number) => `${(value / of) * 100}%`;

  return (
    <div
      ref={frame}
      data-testid="box-frame"
      className="relative cursor-crosshair touch-none overflow-hidden rounded-md select-none"
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={() => setAnchor(null)}
      // A cancelled touch (a scroll takes over, the browser drops the pointer) must end the drag,
      // or later hover moves keep redrawing the box.
      onPointerCancel={() => setAnchor(null)}
      onLostPointerCapture={() => setAnchor(null)}
    >
      <img
        src={src}
        alt="Latest capture"
        draggable={false}
        className="block w-full"
        onLoad={(e) =>
          onNatural({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })
        }
      />
      {/* Drawn only when it fits, as `resolveCrop` applies it on every other capture screen. */}
      {box && natural && resolveCrop(box, natural) && (
        <div
          data-testid="box"
          className="absolute border-2 border-blue-500 shadow-[0_0_0_9999px_rgba(17,24,39,0.45)]"
          style={{
            left: pct(box[0], natural.width),
            top: pct(box[1], natural.height),
            width: pct(box[2], natural.width),
            height: pct(box[3], natural.height),
          }}
        >
          {CORNERS.map(({ corner, className }) => (
            <span
              key={corner}
              data-corner={corner}
              aria-label={`Resize from the ${corner} corner`}
              className={`absolute size-3 rounded-sm border-2 border-blue-500 bg-white ${className}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const FIELDS = ["x", "y", "w", "h"] as const;

function BoxInputs({
  box,
  onChange,
}: {
  box: BoundingArea | null;
  onChange: (box: BoundingArea | null) => void;
}) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {FIELDS.map((field, index) => (
        <label key={field} className="text-xs text-gray-500">
          {field}
          {/* Text, not `type="number"`: a number input keeps whatever the DOM shows while the
              value is numerically equal, so typing after the 0 left "01920" on screen. */}
          <Input
            type="text"
            inputMode="numeric"
            aria-label={`Box ${field}`}
            value={box ? String(box[index]) : ""}
            onChange={(e) => {
              const next: BoundingArea = box ? [...box] : [0, 0, 0, 0];
              next[index] = Number(e.target.value.replace(/\D/g, ""));
              onChange(next);
            }}
          />
        </label>
      ))}
    </div>
  );
}

/** The scales half of the office test: what the load cell said over the last 15 minutes. */
function ScalesCheck({ deviceId }: { deviceId: string }) {
  const headers = useApiHeaders();
  const samples = useQuery<TelemetrySamples, Error>({
    queryKey: ["admin", "devices", deviceId, "telemetry"],
    queryFn: () =>
      fetchJson<TelemetrySamples>(
        `${API_URL}/api/v1/admin/monitoring/devices/${deviceId}/telemetry/samples?minutes=15`,
        headers,
      ),
    retry: false,
    // A technician puts a known weight on the bench and watches; the device beats once a minute.
    refetchInterval: 30_000,
  });

  const all = samples.data?.minutes.flatMap((m) => m.weight_samples) ?? [];
  const answered = all.filter((g): g is number => g !== null);
  // The latest second, answered or not: a scale that has just stopped answering must not still
  // read as a weight.
  const latest = all.length ? all[all.length - 1] : null;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium tracking-wide text-gray-400 uppercase">Scales</p>
      {samples.isError && <p className="text-red-700">{errorDetail(samples.error)}</p>}
      {samples.data &&
        (all.length === 0 ? (
          <p className="text-gray-500">No weight samples in the last 15 minutes.</p>
        ) : (
          <p data-testid="scales-summary" className="text-gray-700">
            {answered.length === 0
              ? "The scale answered none of the last 15 minutes."
              : `Latest ${latest === null ? "no answer" : kg(latest / 1000)} · range ${formatNumber("en", Math.min(...answered) / 1000, { maximumFractionDigits: 3 })}–${kg(Math.max(...answered) / 1000)}`}
            {" · "}
            {all.length - answered.length} of {all.length} seconds unanswered
          </p>
        ))}
    </div>
  );
}
